import './list-table.scss';

import { ConfigProvider, Table, type TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';

import { LocalizedLink } from '../localized-link';

/**
 * Поля ячеек 9px × 16px; высоту строки (60px, шапки — 56px) задают стили,
 * чтобы и двухстрочные ячейки укладывались в неё, — как в таблицах лизингового кабинета.
 */
const TABLE_THEME = {
  components: { Table: { cellPaddingBlock: 9, cellPaddingInline: 16 } }
};

interface IProps<TRow> extends Omit<TableProps<TRow>, 'pagination'> {
  columns: ColumnsType<TRow>;
  /**
   * Путь экрана строки (из ROUTES, без языка). Каждая ячейка становится ссылкой —
   * строку можно открыть кликом, Ctrl+кликом или средней кнопкой в новой вкладке.
   */
  rowLink?: (row: TRow) => string | undefined;
}

/** Оборачивает содержимое ячеек колонок (и вложенных групп) в ссылку строки. */
const withRowLink = <TRow,>(
  columns: ColumnsType<TRow>,
  rowLink: (row: TRow) => string | undefined
): ColumnsType<TRow> =>
  columns.map((column) => {
    if ('children' in column && column.children) {
      return { ...column, children: withRowLink(column.children, rowLink) };
    }

    return {
      ...column,
      render: (value: unknown, row: TRow, index: number) => {
        const content = column.render
          ? column.render(value, row, index)
          : (value as React.ReactNode);
        const href = rowLink(row);

        return href ? (
          <LocalizedLink to={href} className="list-table__link">
            {content as React.ReactNode}
          </LocalizedLink>
        ) : (
          content
        );
      }
    };
  });

/**
 * Таблица реестра на antd `Table`: без встроенной пагинации (она общая у списка),
 * широкая таблица прокручивается внутри своего контейнера, а не всей страницей.
 */
const ListTable = <TRow extends object>({
  columns,
  rowLink,
  ...props
}: IProps<TRow>): React.ReactElement => (
  <ConfigProvider theme={TABLE_THEME}>
    <Table<TRow>
      {...props}
      className={classNames('list-table', props.className)}
      columns={rowLink ? withRowLink(columns, rowLink) : columns}
      pagination={false}
      scroll={{ x: 'max-content', ...props.scroll }}
      showSorterTooltip={false}
    />
  </ConfigProvider>
);

export default ListTable;
